"use strict";

const relationsModule = require("js/relations");
const css = require("js/css_constants");
const utils = require("js/util");
const viewUtils = require("js/editor/card_view_utils");

const SELECTOR_RELATION = ".view-relation-shadow";
const SELECTOR_BORDER_MARKER = ".border-marker";
const SELECTOR_CARD = ".view-card";
const SELECTOR_BORDER_MARKERS = SELECTOR_CARD + " " + SELECTOR_BORDER_MARKER;
const SELECTOR_RELATION_DRAGGER = ".relation-dragger";
const SELECTOR_ARROW_TIP_DRAGGER = ".arrow-tip-dragger";

const WHAT_DRAGGING_RELATION = "dragging-relation";
const WHAT_DRAGGING_ARROW_TIP = "dragging-arrow-tip";

const OVERLAP_PADDING = 8;

const STEP_LENGTH = 20;
const ATTACH_SPACING = 20;

const ARROW_TIP_BASE_LENGTH = 10;
const ARROW_TIP_BASE_HEIGHT = 4;

const ARROW_START = "start";
const ARROW_END = "end";

const OBJECT_CARD = "object-card";
const OBJECT_RELATION = "object-relation";

const RECONSTRUCT_OBJECT_FROM = "from";
const RECONSTRUCT_OBJECT_TO = "to";

// abstract class
class ObjectInfo {
    constructor(object, kind) {
        this.id = object.dataset.id;
        this.kind = kind;

        this.__object = object;
        this.__attachedRelations = [];
    }

    getIsInsideSubview() {
        return false;
    }

    getObjectInfoId() {
        throw new Error("abstract method");
    }

    getRelationsAtSide(pos) {
        return this.__attachedRelations[pos];
    }

    attachRelationAtPos(relation) {
        const hasReference = this.__attachedRelations.find((attached) => attached.id == relation.id);

        if (hasReference == null) {
            this.__attachedRelations.push(relation);
        }
    }

    getDefaultAttachPointForRelation(relation) {
        return this.__computeProvisionalAttachPoint();
    }

    __computeProvisionalAttachPoint() {
        throw new Error("abstract method");
    }

    computeAttachPoint() {
        throw new Error("abstract method");
    }

    computeRelationObjectDepth() {
        throw new Error("abstract method");
    }

    computeLeftRightMiddle() {
        throw new Error("abstract method");
    }

    computeTopBottomMiddle() {
        throw new Error("abstract method");
    }

    computeCenter() {
        return { y: this.computeTopBottomMiddle(), x: this.computeLeftRightMiddle() };
    }

    getTop() {
        throw new Error("abstract method");
    }

    getBottom() {
        throw new Error("abstract method");
    }
}

class CardObjectInfo extends ObjectInfo {
    constructor(object) {
        super(object, OBJECT_CARD);

        this.__objectId = object.dataset.id;
        this.__bounds = utils.getBoundingClientRectRelativeToParent(object);

        this.__isInsideSubview = object.classList.contains("subview-card");
    }

    getObjectInfoId() {
        return this.__objectId;
    }

    getIsInsideSubview() {
        return this.__isInsideSubview;
    }

    __computeProvisionalAttachPoint() {
        return this.computeCenter();
    }

    /*
     * Will either use a predefined manual attach position, or will draw the arrow so that it will
     * point to the center of the card
     */
    computeAttachPoint() {
        for (let i = 0; i < this.__attachedRelations.length; i++) {
            const relation = this.__attachedRelations[i];

            const { x1, y1, x2, y2 } = relation.getCenterPositions();

            let { x: xInter, y: yInter } = this.__computeIntersection(x1, y1, x2, y2);

            const preset = relation.getPresetCardAttachPointAt(this);

            if (preset.relX != null) {
                xInter = this.__bounds.left + this.__bounds.width * preset.relX;
            }

            if (preset.relY != null) {
                yInter = this.__bounds.top + this.__bounds.height * preset.relY;
            }

            relation.setPos(this, xInter, yInter);
        }
    }

    __computeIntersection(x1, y1, x2, y2) {
        const right = this.__checkSide(
            x1,
            y1,
            x2,
            y2,
            this.__bounds.right,
            this.__bounds.top,
            this.__bounds.right,
            this.__bounds.bottom,
        );
        if (typeof right == "object") {
            return right;
        }

        const left = this.__checkSide(
            x1,
            y1,
            x2,
            y2,
            this.__bounds.left,
            this.__bounds.top,
            this.__bounds.left,
            this.__bounds.bottom,
        );
        if (typeof left == "object") {
            return left;
        }

        const top = this.__checkSide(
            x1,
            y1,
            x2,
            y2,
            this.__bounds.right,
            this.__bounds.top,
            this.__bounds.left,
            this.__bounds.top,
        );
        if (typeof top == "object") {
            return top;
        }

        const bottom = this.__checkSide(
            x1,
            y1,
            x2,
            y2,
            this.__bounds.right,
            this.__bounds.bottom,
            this.__bounds.left,
            this.__bounds.bottom,
        );
        if (typeof bottom == "object") {
            return bottom;
        }

        // special case: The two cards the relation is being drawn between overlap so that the relation
        // does not intersect with any border at all. In this case in order to keep the code simple
        // we simply use the center point of the current card as the endpoint at this card for drawing
        // the relation
        return this.computeCenter();
    }

    // computes intersection between line segments, see "Given two points on each line segment" here for math:
    // https://en.wikipedia.org/wiki/Line%E2%80%93line_intersection
    __checkSide(x1, y1, x2, y2, x3, y3, x4, y4) {
        const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / ((x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4));
        const u = ((x1 - x3) * (y1 - y2) - (y1 - y3) * (x1 - x2)) / ((x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4));

        const hasIntersection = 0 <= t && t <= 1 && 0 <= u && u <= 1;

        if (hasIntersection) {
            // snap to pixel for the horizontal or vertical line, depending on where exactly we connect to the
            // card borders
            let px = 0;
            if (x3 == x4) {
                px = x3;
            } else {
                px = x1 + t * (x2 - x1);
            }

            let py = 0;
            if (y3 == y4) {
                py = y3;
            } else {
                py = y1 + t * (y2 - y1);
            }

            return { x: px, y: py };
        } else {
            return false;
        }
    }

    computeRelationObjectDepth() {
        return 0;
    }

    computeLeftRightMiddle() {
        return this.__bounds.left + this.__bounds.width / 2;
    }

    computeTopBottomMiddle() {
        return this.__bounds.top + this.__bounds.height / 2;
    }

    getTop() {
        return this.__bounds.top;
    }

    getBottom() {
        return this.__bounds.bottom;
    }
}

class RelationObjectInfo extends ObjectInfo {
    constructor(object) {
        super(object, OBJECT_RELATION);
    }

    getObjectInfoId() {
        return this.id;
    }

    putRelationInfo(relationInfos) {
        this.__relationInfo = relationInfos.get(this.id);
    }

    __computeProvisionalAttachPoint() {
        return {
            x: this.computeLeftRightMiddle(),
            y: this.computeTopBottomMiddle(),
        };
    }

    computeAttachPoint() {
        if (this.__attachedRelations.length == 0) {
            return;
        }

        this.__attachedRelations.forEach((relation) => {
            const fraction = relation.getPresetRelationAttachPointAt(this);

            const xAttach = this.computeLeftRightMiddle(fraction);
            const yAttach = this.computeTopBottomMiddle(fraction);

            relation.setPos(this, xAttach, yAttach);
        });
    }

    computeRelationObjectDepth() {
        if (this.__relationInfo == null) {
            console.warn(
                "Cannot compute relation depth, does not have a relation info! " +
                    "Most likely reason is that this relation points to an object that no does not exist",
            );
            return "cannot_compute";
        } else {
            return this.__relationInfo.computeRelOnRelDepth() + 1;
        }
    }

    computeLeftRightMiddle(fraction) {
        return this.__relationInfo.computeXMiddle(fraction);
    }

    computeTopBottomMiddle(fraction) {
        return this.__relationInfo.computeYMiddle(fraction);
    }

    getTop() {
        return this.computeTopBottomMiddle();
    }

    getBottom() {
        return this.computeTopBottomMiddle();
    }
}

class RelationInfo {
    constructor(relation, objectStart, objectEnd, liveViewElement) {
        this.id = relation.dataset.id;
        this.__relation = relation;

        this.main = this.__relation.querySelector(".view-relation");
        this.shadow = this.__relation.querySelector(".view-relation-shadow");
        this.arrowTipsContainer = this.__relation.querySelector(".arrow-tip-container");
        this.arrowTips = this.arrowTipsContainer.querySelector("path.arrow-tip");
        // clear the path attribute cause we might need to connect multiple arrow tips into
        // a single svg element
        this.arrowTips.setAttribute("d", "");

        this.fromDragger = liveViewElement.querySelector(
            `${SELECTOR_RELATION_DRAGGER}[data-relation-id="${this.__relation.dataset.id}"][data-from-to="from"]`,
        );
        this.toDragger = liveViewElement.querySelector(
            `${SELECTOR_RELATION_DRAGGER}[data-relation-id="${this.__relation.dataset.id}"][data-from-to="to"]`,
        );

        if (this.toDragger == null) {
            throw new Error("To Dragger missing");
        }
        if (this.fromDragger == null) {
            throw new Error("From Dragger missing");
        }
        if (objectStart == null) {
            throw new Error("objectStart cannot be null");
        }

        this.__objectStart = objectStart;

        if (objectEnd == null) {
            throw new Error("objectStart cannot be null");
        }
        this.__objectEnd = objectEnd;

        // reconstruct from information stored in relation directly, used for dragging relations around
        if (objectStart == RECONSTRUCT_OBJECT_FROM && objectEnd == RECONSTRUCT_OBJECT_TO) {
            this.__pos = {
                start: {
                    x: parseInt(this.__relation.dataset.fromX),
                    y: parseInt(this.__relation.dataset.fromY),
                },
                end: {
                    x: parseInt(this.__relation.dataset.toX),
                    y: parseInt(this.__relation.dataset.toY),
                },
            };
        } else {
            this.__pos = {
                start: {
                    x: Number.NaN,
                    y: Number.NaN,
                },
                end: {
                    x: Number.NaN,
                    y: Number.NaN,
                },
            };
        }
    }

    getIsInsideSubview() {
        return this.__objectStart.getIsInsideSubview() && this.__objectEnd.getIsInsideSubview();
    }

    getFrom() {
        return this.__objectStart;
    }

    getTo() {
        return this.__objectEnd;
    }

    getCenterPositions() {
        const { x: x1, y: y1 } = this.__objectStart.computeCenter();
        const { x: x2, y: y2 } = this.__objectEnd.computeCenter();

        return {
            x1: x1,
            y1: y1,
            x2: x2,
            y2: y2,
        };
    }

    getOther(object) {
        if (this.__objectStart == object) {
            return this.__objectEnd;
        } else if (this.__objectEnd == object) {
            return this.__objectStart;
        } else {
            throw new Error("passed object is neither start nor end object");
        }
    }

    getObjectId(object) {
        if (this.__getFromTo(object) == "from") {
            return this.__relation.dataset.from;
        } else {
            return this.__relation.dataset.to;
        }
    }

    __isStart(object) {
        return this.__objectStart == object;
    }

    __isEnd(object) {
        return this.__objectEnd == object;
    }

    setPos(object, x, y) {
        const pos = this.__getSide(object);
        const overwritePos = this.__getOverwritePosition(object);

        if (overwritePos == null) {
            this.__pos[pos].x = x;
            this.__pos[pos].y = y;
        } else {
            this.__pos[pos].x = overwritePos.x;
            this.__pos[pos].y = overwritePos.y;
        }

        // TODO Kiru: check with clemens if this is the correct place
        if (this.getIsInsideSubview()) {
            // this.__pos[pos].x -= object.__bounds.x;
            // this.__pos[pos].y -= object.__bounds.y;
        }
    }

    __getOverwritePosition(object) {
        let vals;

        if (this.__getFromTo(object) == "from") {
            vals = {
                x: this.__relation.dataset.overwriteFromX,
                y: this.__relation.dataset.overwriteFromY,
            };
        } else {
            vals = {
                x: this.__relation.dataset.overwriteToX,
                y: this.__relation.dataset.overwriteToY,
            };
        }

        if (vals.x == undefined || vals.x == "") {
            return null;
        } else {
            return {
                x: parseFloat(vals.x),
                y: parseFloat(vals.y),
            };
        }
    }

    computeXMiddle(fraction) {
        fraction = fraction == null ? 0.5 : fraction;
        let [start, end] = this.__getStartAndEndPos();
        return start.x + (end.x - start.x) * fraction;
    }

    computeYMiddle(fraction) {
        fraction = fraction == null ? 0.5 : fraction;
        let [start, end] = this.__getStartAndEndPos();
        return start.y + (end.y - start.y) * fraction;
    }

    __getStartAndEndPos() {
        const overwritePosStart = this.__getOverwritePosition(this.__objectStart);
        const knownPosStart = isNaN(this.__pos.start.x)
            ? this.__objectStart.getDefaultAttachPointForRelation(this)
            : this.__pos.start;
        const start = overwritePosStart == null ? knownPosStart : overwritePosStart;

        const overwritePosEnd = this.__getOverwritePosition(this.__objectEnd);
        const knownPosEnd = isNaN(this.__pos.end.x)
            ? this.__objectEnd.getDefaultAttachPointForRelation(this)
            : this.__pos.end;
        const end = overwritePosEnd == null ? knownPosEnd : overwritePosEnd;

        return [start, end];
    }

    __getSide(object) {
        if (object == this.__objectStart) {
            return "start";
        } else if (object == this.__objectEnd) {
            return "end";
        } else {
            throw new Error("object is neither start nor end object");
        }
    }

    __getFromTo(object) {
        return this.__getSide(object) == "start" ? "from" : "to";
    }

    computeRelOnRelDepth() {
        const startDepth = this.__objectStart.computeRelationObjectDepth();
        const endDepth = this.__objectEnd.computeRelationObjectDepth();

        if (typeof startDepth == "string" || typeof endDepth == "string") {
            return "cannot_compute";
        }

        if (startDepth > 0 && endDepth > 0) {
            throw new Error(
                "Assumption error: Expected only start or end depth to be greater than zero, but found start: " +
                    startDepth +
                    ", end: " +
                    endDepth,
            );
        } else {
            return Math.max(startDepth, endDepth);
        }
    }

    get fromX() {
        return this.__pos.start.x;
    }

    get fromY() {
        return this.__pos.start.y;
    }

    get toX() {
        return this.__pos.end.x;
    }

    get toY() {
        return this.__pos.end.y;
    }

    get length() {
        return computeLength(this.fromX, this.fromY, this.toX, this.toY);
    }

    get angle() {
        return computeAngle(this.fromX, this.fromY, this.toX, this.toY);
    }

    getCurrentX(where) {
        if (this.__getFromTo(where) == "from") {
            return parseFloat(this.__relation.dataset.fromX);
        } else if (this.__getFromTo(where) == "to") {
            return parseFloat(this.__relation.dataset.toX);
        } else {
            throw new Error("unknown side: " + side);
        }
    }

    getCurrentY(where) {
        if (this.__getFromTo(where) == "from") {
            return parseFloat(this.__relation.dataset.fromY);
        } else if (this.__getFromTo(where) == "to") {
            return parseFloat(this.__relation.dataset.toY);
        } else {
            throw new Error("unknown side: " + side);
        }
    }

    invertFromAndTo() {
        let temp = this.__pos.start;
        this.__pos.start = this.__pos.end;
        this.__pos.end = temp;

        temp = this.__objectStart;
        this.__objectStart = this.__objectEnd;
        this.__objectEnd = temp;
    }

    forMainPaths(func) {
        func(this.main);
        func(this.shadow);
    }

    persistFromTo() {
        this.__relation.setAttribute("data-from-x", this.fromX);
        this.__relation.setAttribute("data-from-y", this.fromY);
        this.__relation.setAttribute("data-to-x", this.toX);
        this.__relation.setAttribute("data-to-y", this.toY);
    }

    getPresetCardAttachPointAt(object) {
        const preset = this.__getPresetFromTo(object);

        let relX = null;
        let relY = null;

        if (preset.startsWith("card")) {
            const split = preset.split(";");

            const x = split[1].toLowerCase().startsWith("x") ? split[1] : split[2];
            const y = split[2].toLowerCase().startsWith("y") ? split[2] : split[1];

            relX = parseFloat(x.split("=")[1]);
            relY = parseFloat(y.split("=")[1]);
        } else if (preset.startsWith("border")) {
            const split = preset.split(";");
            let where = split[1].toLowerCase().startsWith("at") ? split[1] : split[2];
            let fraction = split[2].toLowerCase().startsWith("fraction") ? split[2] : split[1];

            where = where.split("=")[1].toLowerCase();
            fraction = parseFloat(fraction.split("=")[1]);

            switch (where) {
                case "left":
                    relX = 0;
                    relY = fraction;
                    break;
                case "right":
                    relX = 1;
                    relY = fraction;
                    break;
                case "top":
                    relX = fraction;
                    relY = 0;
                    break;
                case "bottom":
                    relX = fraction;
                    relY = 1;
                    break;
                default:
                    throw new Error("Unknown value for border side: " + where);
            }
        }

        return {
            relX: relX,
            relY: relY,
        };
    }

    getPresetRelationAttachPointAt(object) {
        const preset = this.__getPresetFromTo(object);

        if (preset.startsWith("relation")) {
            const split = preset.split(";");
            return parseFloat(split[1].split("=")[1]);
        } else {
            return null;
        }
    }

    __getPresetFromTo(object) {
        const attachPos = utils.camelize("attach_" + this.__getFromTo(object));
        return this.__relation.dataset[attachPos];
    }

    setOverwritePosition(object, x, y) {
        const fromTo = this.__getFromTo(object);

        if (fromTo == "from") {
            this.__relation.dataset.overwriteFromX = x;
            this.__relation.dataset.overwriteFromY = y;
        } else {
            this.__relation.dataset.overwriteToX = x;
            this.__relation.dataset.overwriteToY = y;
        }
    }

    setArrowTipAdjust(fromTo, x, y) {
        if (fromTo == "from") {
            this.__relation.dataset.arrowTipFromAdjustX = x;
            this.__relation.dataset.arrowTipFromAdjustY = y;
        } else {
            this.__relation.dataset.arrowTipToAdjustX = x;
            this.__relation.dataset.arrowTipToAdjustY = y;
        }
    }

    getArrowTipAdjust(fromTo) {
        let x;
        let y;

        if (fromTo == "from") {
            x = this.__relation.dataset.arrowTipFromAdjustX;
            y = this.__relation.dataset.arrowTipFromAdjustY;
        } else {
            x = this.__relation.dataset.arrowTipToAdjustX;
            y = this.__relation.dataset.arrowTipToAdjustY;
        }

        x = parseFloat(x);
        x = isNaN(x) ? 0 : x;

        y = parseFloat(y);
        y = isNaN(y) ? 0 : y;

        return {
            x: x,
            y: y,
        };
    }

    // disable pointer events for all elements of the relation during drag and drop
    disableElementInteractions() {
        this.main.style.pointerEvents = "none";
        this.shadow.style.pointerEvents = "none";

        // z-index of draggers must be 1, so that it can be selected while on top of card. However
        // during dragging it must be 0, because otherwise the border highlighting will not receive
        // the hover events and thus not light up
        this.fromDragger.style.zIndex = 0;
        this.fromDragger.style.pointerEvents = "none";

        this.toDragger.style.zIndex = 0;
        this.toDragger.style.pointerEvents = "none";
    }

    resetElementInteractions() {
        this.main.style.pointerEvents = null;
        this.shadow.style.pointerEvents = null;

        this.fromDragger.style.zIndex = null;
        this.fromDragger.style.pointerEvents = null;

        this.toDragger.style.zIndex = null;
        this.toDragger.style.pointerEvents = null;
    }
}

function computeLength(fromX, fromY, toX, toY) {
    const lengthX = toX - fromX;
    const lengthY = toY - fromY;

    return Math.max(0, Math.floor(Math.sqrt(lengthX * lengthX + lengthY * lengthY)));
}

function computeAngle(fromX, fromY, toX, toY) {
    const lengthX = toX - fromX;
    const lengthY = toY - fromY;

    return (Math.atan2(lengthY, lengthX) * 180) / Math.PI;
}

function computeAttachPoints(objects) {
    objects.forEach((object) => {
        object.computeAttachPoint();
    });
}

function positionRelations(relationInfos) {
    if (relationInfos instanceof Map) {
        for (let relation of relationInfos.values()) {
            buildPath(relation);
        }
    } else {
        buildPath(relationInfos);
    }
}

function buildPath(pathData) {
    switch (utils.camelize(pathData.main.dataset.type)) {
        case relationsModule.afterRight:
            pathWave(pathData, false, true);
            break;
        case relationsModule.afterLeft:
            pathWave(pathData, true, false);
            break;
        case relationsModule.closedLoop:
            pathWave(pathData, true, true);
            break;
        case relationsModule.supportsRight:
            pathSupport(pathData, ARROW_END);
            break;
        case relationsModule.supportsLeft:
            pathSupport(pathData, ARROW_START);
            break;
        case relationsModule.weakensRight:
            pathWeakens(pathData, ARROW_END);
            break;
        case relationsModule.weakensLeft:
            pathWeakens(pathData, ARROW_START);
            break;
        case relationsModule.equals:
            pathEqual(pathData);
            break;
        case relationsModule.notEquals:
            pathNotEqual(pathData);
            break;
        case relationsModule.objects: // aka opposes
            pathObjects(pathData);
            break;
        case relationsModule.question:
            pathWave(pathData, false, false);
            break;
        case relationsModule.connection:
        default:
            pathConnection(pathData);
    }

    const draggerBounds = pathData.fromDragger.getBoundingClientRect();

    pathData.fromDragger.style.left = `${pathData.fromX - draggerBounds.width / 2}px`;
    pathData.fromDragger.style.top = `${pathData.fromY - draggerBounds.height / 2}px`;
    pathData.toDragger.style.left = `${pathData.toX - draggerBounds.width / 2}px`;
    pathData.toDragger.style.top = `${pathData.toY - draggerBounds.height / 2}px`;

    pathData.persistFromTo();
}

function pathConnection(pathData) {
    const [pathDescription, transform, attachPoints] = computeConnection(pathData);

    pathData.forMainPaths((path) => {
        path.setAttribute("d", pathDescription);
        path.setAttribute("transform", transform);
    });

    return [transform, attachPoints];
}

function pathSupport(pathData, arrowPos) {
    const [transform, attachPoints] = pathConnection(pathData);
    addArrowMarker(pathData, arrowPos, transform, attachPoints);
}

function pathWeakens(pathData, arrowPos) {
    pathSupport(pathData, arrowPos);
}

// aka pathContradiction
// the new-kid-around-the-corner variant: A/\/\/\B
function pathObjects(pathData) {
    const height = 3;
    const width = 4;

    const [pathDescription, transform, ap] = computeConnection(pathData);

    // draw /
    let d = `M0,0 l${height},${width} `;

    let end = Math.ceil(ap.end[0] / height);
    for (let i = 0; i < end - 2; i++) {
        if (i % 2 == 0) {
            // draw /
            d += ` ${height},${-width} `;
        } else {
            // draw \
            d += ` ${height},${width}  `;
        }
    }

    // the last past is connected to the end
    d += ` L${ap.end[0]},${ap.end[1]}  `;

    pathData.forMainPaths((path) => {
        path.setAttribute("d", d);
        path.setAttribute("transform", transform);
    });
}

// this is the old variant where you had this: A -><- B
function pathObjectsArrowMiddleVariant(pathData) {
    const gap = 1;

    const [pathDescription, transform, ap] = computeConnection(pathData);

    // two arrows from the middle to the cards, so that we can simply attach the same arrow heads
    // at the start and end
    let d = `M${ap.middle[0] + gap},${ap.middle[1]} L${ap.end[0]},${ap.end[1]} M0,0 L${ap.middle[0] - gap},${
        ap.middle[1]
    }`;

    addArrowMarkerMiddle(pathData, ap.middle[0], ap.middle[1], gap, transform);

    pathData.forMainPaths((path) => {
        path.setAttribute("d", d);
        path.setAttribute("transform", transform);
    });
}

function pathEqual(pathData) {
    const [d, transform] = computeEqualConnection(pathData);

    pathData.forMainPaths((path) => {
        path.setAttribute("d", d);
        path.setAttribute("transform", transform);
    });
}

function pathNotEqual(pathData) {
    const yShift = 4;
    const xShift = 2;

    let [d, transform, attachPointsA, attachPointsB] = computeEqualConnection(pathData);

    pathData.shadow.setAttribute("d", d);

    // add lines at the attach points
    const a = attachPointsA.steps.length < attachPointsB.steps.length ? attachPointsA.steps : attachPointsB.steps;
    const b = attachPointsA.steps.length < attachPointsB.steps.length ? attachPointsB.steps : attachPointsA.steps;

    for (let i = 0; i < a.length; ++i) {
        const x1 = a[i][0] + xShift;
        const y1 = a[i][1] + yShift;

        const x2 = b[i][0] - xShift;
        const y2 = b[i][1] - yShift;

        d += ` M${x1},${y1} L${x2},${y2}`;
    }

    pathData.main.setAttribute("d", d);

    pathData.forMainPaths((path) => path.setAttribute("transform", transform));
}

function pathWave(pathData, arrowStart, arrowEnd) {
    // we would like the wavy part to start at the edge of the card, so if
    // the arrow tip should be at the start we invert the direction of drawing
    if (arrowStart && !arrowEnd) {
        pathData.invertFromAndTo();
    }

    let [length, arrowLength, step, angle] = computeBaseLengths(pathData);
    step = Math.floor(step / 2);

    let firstStep;
    if (arrowStart && arrowEnd) {
        firstStep = arrowLength;
        length -= arrowLength;
    } else {
        firstStep = 0;
    }

    const from = [0, 0];

    let d = `M${from[0]},${from[1]} Q0,0 ${firstStep},0 q${step / 2},${step / 2} ${step},0 `;
    let l = step;

    // track if we need to draw an upwards curve for the remain at the end or a downwards curve
    // alternate between 1 and 2, cause there is no logical xor operator in JS
    let down = 1;

    // attach arcs until we have nearly reached the length of the arrow
    // step > 0 cause otherwise we get an allocation size overflow
    while (l + step < length && step > 0) {
        d += `t ${step},0 `;
        l += step;
        down = down ^ 1;
    }

    // draw the missing part of the line
    if (l < length) {
        const remain = (length - l) / 2;
        if (down == 1) {
            d += `q${remain},${-remain} ${2 * remain},${0} `;
        } else {
            d += `q${remain},${remain} ${2 * remain},${0} `;
        }

        l += 2 * remain;
    }

    // append one short piece of line, so that the arrow looks like it is attached in a straigt line to the path
    d += ` l${arrowLength},0`;
    l += arrowLength + firstStep;

    // move the line to the correct position, and rotate it so that it connects the two cards
    const transform = `rotate (${angle}, ${pathData.fromX}, ${pathData.fromY}) translate(${pathData.fromX}, ${pathData.fromY})`;

    pathData.forMainPaths((path) => {
        path.setAttribute("d", d);
        path.setAttribute("transform", transform);
    });

    const arrowPositions = {
        arrowAttachStart: from,
        arrowAttachEnd: [l, 0],
    };

    if (arrowStart || arrowEnd) {
        addArrowMarker(pathData, ARROW_END, transform, arrowPositions);
        if (arrowStart && arrowEnd) {
            addArrowMarker(pathData, ARROW_START, transform, arrowPositions);
        }
    }
}

function addArrowMarker(pathData, arrowPos, transform, { arrowAttachStart: start, arrowAttachEnd: end } = {}) {
    let [tipLength, tipHeight] = computeTipLengthHeight(pathData, 2);

    if (arrowPos === ARROW_START) {
        [tipLength, tipHeight] = adjustTipLenghtHeight(pathData, "from", tipLength, tipHeight);

        pathData.arrowTips.setAttribute(
            "d",
            getPath(pathData.arrowTips) +
                ` M ${start[0]},${
                    start[1]
                } l${tipLength},${tipHeight} l${-tipLength},${-tipHeight} l${tipLength},${-tipHeight}`,
        );

        const circleOne = pathData.arrowTipsContainer.querySelector("circle[data-from-to=from][data-invert=true]");
        circleOne.setAttribute("transform", `translate(${start[0] + tipLength}, ${start[1] + tipHeight})`);
        // circleOne.classList.remove(css.disabled);

        const circleTwo = pathData.arrowTipsContainer.querySelector("circle[data-from-to=from][data-invert=false]");
        circleTwo.setAttribute("transform", `translate(${start[0] + tipLength}, ${start[1] - tipHeight})`);
        // circleTwo.classList.remove(css.disabled);

        pathData.shadow.setAttribute(
            "d",
            pathData.main.getAttribute("d") +
                ` M ${start[0]},${
                    start[1]
                } l${tipLength},${tipHeight} l${-tipLength},${-tipHeight} l${tipLength},${-tipHeight}`,
        );
    } else if (arrowPos === ARROW_END) {
        [tipLength, tipHeight] = adjustTipLenghtHeight(pathData, "to", tipLength, tipHeight);

        pathData.arrowTips.setAttribute(
            "d",
            getPath(pathData.arrowTips) +
                ` M ${end[0]},${
                    end[1]
                } l${-tipLength},${tipHeight} m${tipLength},${-tipHeight} l${-tipLength},${-tipHeight}`,
        );

        const circleOne = pathData.arrowTipsContainer.querySelector("circle[data-from-to=to][data-invert=true]");
        circleOne.setAttribute("transform", `translate(${end[0] - tipLength}, ${end[1] + tipHeight})`);
        // circleOne.classList.remove(css.disabled);

        const circleTwo = pathData.arrowTipsContainer.querySelector("circle[data-from-to=to][data-invert=false]");
        circleTwo.setAttribute("transform", `translate(${end[0] - tipLength}, ${end[1] - tipHeight})`);
        // circleTwo.classList.remove(css.disabled);

        pathData.shadow.setAttribute(
            "d",
            pathData.main.getAttribute("d") +
                ` l${-tipLength},${tipHeight} m${tipLength},${-tipHeight} l${-tipLength},${-tipHeight}`,
        );
    } else {
        throw new Error("Unknown error position: " + arrowPos);
    }

    pathData.arrowTipsContainer.setAttribute("transform", transform);
}

function addArrowMarkerMiddle(pathData, middleX, middleY, gap, transform) {
    let [tipLength, tipHeight] = computeTipLengthHeight(pathData, 4);
    const originalTipLength = tipLength;
    const originalTipHeight = tipHeight;

    [tipLength, tipHeight] = adjustTipLenghtHeight(pathData, "from", tipLength, tipHeight);

    // add > in the middle
    let leftArrow = ` M ${
        middleX + gap
    },${middleY} l${tipLength},${tipHeight} l${-tipLength},${-tipHeight} l${tipLength},${-tipHeight}`;

    const circleOne = pathData.arrowTipsContainer.querySelector("circle[data-from-to=from][data-invert=true]");
    circleOne.setAttribute("transform", `translate(${middleX + tipLength}, ${middleY + tipHeight})`);
    // circleOne.classList.remove(css.disabled);

    const circleTwo = pathData.arrowTipsContainer.querySelector("circle[data-from-to=from][data-invert=false]");
    circleTwo.setAttribute("transform", `translate(${middleX + tipLength}, ${middleY - tipHeight})`);
    // circleTwo.classList.remove(css.disabled);

    [tipLength, tipHeight] = adjustTipLenghtHeight(pathData, "to", originalTipLength, originalTipHeight);

    // add < in the middle
    let rightArrow = ` M ${
        middleX - gap
    },${middleY} l${-tipLength},${tipHeight} m${tipLength},${-tipHeight} l${-tipLength},${-tipHeight}`;

    const circleThree = pathData.arrowTipsContainer.querySelector("circle[data-from-to=to][data-invert=true]");
    circleThree.setAttribute("transform", `translate(${middleX - tipLength}, ${middleY + tipHeight})`);
    // circleThree.classList.remove(css.disabled);

    const circleFour = pathData.arrowTipsContainer.querySelector("circle[data-from-to=to][data-invert=false]");
    circleFour.setAttribute("transform", `translate(${middleX - tipLength}, ${middleY - tipHeight})`);
    // circleFour.classList.remove(css.disabled);

    pathData.arrowTips.setAttribute("d", leftArrow + rightArrow);
    pathData.arrowTipsContainer.setAttribute("transform", transform);
}

function computeTipLengthHeight(pathData, sizeFactor) {
    const length = pathData.length;

    let tipLength, tipHeight;

    if (ARROW_TIP_BASE_LENGTH * sizeFactor > length) {
        tipLength = length / sizeFactor;
        tipHeight = ARROW_TIP_BASE_HEIGHT * (tipLength / ARROW_TIP_BASE_LENGTH);
    } else {
        tipLength = ARROW_TIP_BASE_LENGTH;
        tipHeight = ARROW_TIP_BASE_HEIGHT;
    }

    return [tipLength, tipHeight];
}

function adjustTipLenghtHeight(pathData, fromTo, tipLength, tipHeight) {
    let { x: adjustX, y: adjustY } = pathData.getArrowTipAdjust(fromTo);

    tipLength = tipLength - adjustX;
    tipHeight = tipHeight - adjustY;

    return [tipLength, tipHeight];
}

function computeBaseLengths(pathData) {
    let length = pathData.length;
    const angle = pathData.angle;

    // reduce length by size of arrow length
    const arrowLength = ARROW_TIP_BASE_LENGTH;
    const newLength = length - arrowLength;
    let stepLength = STEP_LENGTH;

    // sanitize length values
    if (newLength < 0) {
        stepLength = Math.floor(length / 2);
        length = 0;
    } else {
        length = newLength;
    }

    return [length, arrowLength, stepLength, angle];
}

function getPath(svg) {
    const d = svg.getAttribute("d");
    return d == null ? "" : d;
}

function computeConnection(pathData, yOffset = 0) {
    let [length, arrowLength, step, angle] = computeBaseLengths(pathData);

    step = arrowLength;

    const start = [step, yOffset];
    const end = [arrowLength + length, yOffset];
    const middle = [end[0] / 2, yOffset];

    const nAttachPoints = Math.floor(middle[0] / step);
    const attachStepSize = middle[0] / nAttachPoints;

    const attachSteps = [];

    attachSteps.push(start);
    for (let i = 1; i < nAttachPoints - 1; i++) {
        attachSteps.push([start[0] + i * attachStepSize, start[1]]);
    }
    attachSteps.push(middle);
    for (let i = 1; i < nAttachPoints - 1; i++) {
        attachSteps.push([middle[0] + i * attachStepSize, start[1]]);
    }
    attachSteps.push(end);

    const from = [0, yOffset];
    const to = [arrowLength + length, 0];

    const pathDescription = `M${from[0]},${from[1]} h${to[0]}`;

    const attachPoints = {
        start: start,
        middle: middle,
        end: end,
        steps: attachSteps,
        stepSize: step,
        arrowAttachStart: from,
        arrowAttachEnd: to,
    };

    // move the line to the correct position, and rotate it so that it connects the two cards
    const transform = `rotate (${angle}, ${pathData.fromX}, ${pathData.fromY}) translate(${pathData.fromX}, ${pathData.fromY})`;

    return [pathDescription, transform, attachPoints];
}

function computeEqualConnection(pathData) {
    const yOffset = 2;

    // create two slighty shifted paths, and join them togehter again for the real path
    const [a, ignoreTransform, attachPointsA] = computeConnection(pathData, -1 * yOffset);
    const [b, transform, attachPointsB] = computeConnection(pathData, yOffset);

    const d = a + " M 0 0 " + b;

    return [d, transform, attachPointsA, attachPointsB];
}

function compLeftRight(a, b) {
    const posA = a.computeLeftRightMiddle();
    const posB = b.computeLeftRightMiddle();
    const left = posA < posB ? a : b;
    const right = left == a ? b : a;
    return { left: left, right: right };
}

function compTopBottom(a, b) {
    const posA = a.computeTopBottomMiddle();
    const posB = b.computeTopBottomMiddle();
    const top = posA < posB ? a : b;
    const bottom = top == a ? b : a;
    return { top: top, bottom: bottom };
}

function isOverlappingYAxis(a, b) {
    let aTop = a.getTop();
    let aBottom = a.getBottom();
    let bTop = b.getTop();
    let bBottom = b.getBottom();

    // special cases if the relation is pointing to another relation
    if (aTop == aBottom) {
        bTop = bTop - 2 * OVERLAP_PADDING;
        bBottom = bBottom + 2 * OVERLAP_PADDING;

        return aTop >= bTop && aTop <= bBottom;
    } else if (bTop == bBottom) {
        aTop = aTop - 2 * OVERLAP_PADDING;
        aBottom = aBottom + 2 * OVERLAP_PADDING;

        return bTop >= aTop && bTop <= aBottom;
    } else {
        // default case, relation between two cards
        aTop = aTop - OVERLAP_PADDING;
        aBottom = aBottom + OVERLAP_PADDING;
        bTop = bTop - OVERLAP_PADDING;
        bBottom = bBottom + OVERLAP_PADDING;

        return (aTop <= bBottom && aTop >= bTop) || (aBottom <= bBottom && aBottom >= bTop);
    }
}

function buildObjectInfo(object) {
    if (object.matches(SELECTOR_CARD)) {
        return new CardObjectInfo(object);
    } else if (object.matches(".relation-shadow-group")) {
        return new RelationObjectInfo(object);
    } else {
        throw new Error("Unable to determine object kind: " + object);
    }
}

function buildObjectInfos(objects) {
    const infos = new Map();

    for (let object of objects) {
        const info = buildObjectInfo(object);

        infos.set(info.id, info);

        if (info.id !== info.getObjectInfoId()) {
            infos.set(info.getObjectInfoId(), info);
        }
    }

    return infos;
}

function buildRelationInfos(relationGroups, objectInfo, liveViewElement) {
    const relationInfo = new Map();

    relationGroups.forEach((group) => {
        group.forEach((rel) => {
            const id = rel.dataset.id;

            if (!relationInfo.has(id)) {
                const start = objectInfo.get(rel.dataset.from);
                const end = objectInfo.get(rel.dataset.to);

                // this should not happen cause the server should not allow this kind of situation,
                // but we've encountered it in the wild. So we just skip those relations, no hurt
                // done
                if (start != null && end != null) {
                    relationInfo.set(id, new RelationInfo(rel, start, end, liveViewElement));
                } else {
                    console.warn("Encountered a relation that connects nonexistent objects, skipping");
                }
            }
        });
    });

    return relationInfo;
}

function putRelationInfos(objectInfo, relationInfo) {
    for (let object of objectInfo.values()) {
        if (object.kind == OBJECT_RELATION) {
            object.putRelationInfo(relationInfo);
        }
    }
}

function attachRelationsToObjects(relationInfo) {
    const from = relationInfo.getFrom();
    const to = relationInfo.getTo();

    from.attachRelationAtPos(relationInfo);
    to.attachRelationAtPos(relationInfo);
}

function sortObjectsByKind(objectInfo) {
    const byKind = [[], []];

    for (const object of objectInfo.values()) {
        switch (object.kind) {
            case OBJECT_RELATION:
                byKind[1].push(object);
                break;
            case OBJECT_CARD:
                byKind[0].push(object);
                break;
            default:
                throw new Error("Unknown relation kind: " + object.kind);
        }
    }

    return byKind.flat();
}

function orderRelationsByRelOnRelLinkDepth(relationInfos) {
    const depths = [];
    const noRender = [];

    for (const relInfo of relationInfos.values()) {
        const depth = relInfo.computeRelOnRelDepth();

        if (typeof depth == "string") {
            console.warn(
                "Encountered a relation on relation where a sub relation points to an object " +
                    "that does not exist. Removing this relation from the list of relations to be rendered!",
            );
            noRender.push(relInfo);
        } else {
            let depthGroup = depths[depth];

            if (depthGroup == null) {
                depthGroup = [];
                depths[depth] = depthGroup;
            }

            depthGroup.push(relInfo);
        }
    }

    return [depths.flat(), noRender];
}

function getAllRelations(liveViewElement) {
    return Array.from(liveViewElement.querySelectorAll("svg g.card-relation-group"))
        .map((group) => group.querySelectorAll("g.relation-shadow-group"))
        .filter((group) => group.length > 0);
}

/*
 * General idea of this algorithm
 * ------------------------------
 *
 * The goals for positioning the relations are as follows:
 *
 *   1. Relations should be easy to distinguish, so they should not all start and end at the same point
 *      of origin for a given card side (i.e. on the right side, all in the middle)
 *   2. Relations should be spaced nicely.
 *   3. If there are too many relations to be comfortably fit to the current side of the card (i.e. you've
 *      got a card with a very narrow top, but need to attach 10 relations), the relations should all be
 *      attached to the top, originating from only a few attachment points, but then give enough space so
 *      that not all relations overlap.
 *   4. Relations should not cross.
 *
 * To meet all those requirements, we do the following:
 *
 *   1. Get all objects where relations can originate from.
 *   2. Get all relations (a relation form here until actual rendering is the group of the two arrows, i.e.
 *      the visible relation and the shadow).
 *   3. For all relation objects (i.e. relations that another relation points to): Connect them with the
 *      rendering info for the relation.
 *   4. Order all relations by the count of how many relations they are recursively pointing to. This is
 *      required so that the computation of the attach points to relation objects already knows the exact
 *      position of the relation in the canvas, so that it can be attached in the middle of the relation.
 *   3. For all objects:
 *      a. Attach/Assign all relations that connect to them, grouped by the side (left/right/top/bottom).
 *      b. Sort the objects by their kind (i.e. card object or relation object). Again this is required so
 *         that the positions of the relations with incoming relations have already been computed.
 *      c. Sort the attached relations per object, ordered according to the angle the relation has relative
 *         to the object. This should ensure that relations originating from the same side of an object do
 *         not cross.
 *   4. For all objects, and all relations for a given side, compute the point where the relation attachs
 *      to the object on screen. Further, if there are more relations then possible attachment points,
 *      compute how much the relations must `bend` in order to give enough space for the other relations.
 *      See `computeAttachPoints` for more details (solve requirements 1-3).
 *   5. Using the given attachment points, render/layout the relations, given the coordinates computed
 *      in the step above.
 */
module.exports.positionRelations = function (liveViewElement, affectedObjects = []) {
    const objects = Array.from(liveViewElement.querySelectorAll(SELECTOR_CARD + ", svg g.relation-shadow-group"));
    let relationGroups = getAllRelations(liveViewElement);

    const objectInfo = buildObjectInfos(objects);
    let relationInfo = buildRelationInfos(relationGroups, objectInfo, liveViewElement);

    /**
     * Updating all relations is expensive, so we allow to pass a subset of objects.
     * If a list of cards is given
     */
    if (affectedObjects.length > 0) {
        // for performance optimization, add all affected objects in a Set
        let objectIds = new Set();
        affectedObjects.forEach((each) => objectIds.add(each.dataset.id));

        // keep only relations, where the affections objects are invovled
        relationInfo.forEach((value, key, map) => {
            const isAffected = objectIds.has(value.__objectStart.id) || objectIds.has(value.__objectEnd.id);
            if (!isAffected) {
                // delete the relation
                map.delete(key);
            }
        });
    }

    putRelationInfos(objectInfo, relationInfo);

    const [relByDepth, relNoRender] = orderRelationsByRelOnRelLinkDepth(relationInfo);
    // delete those relations that cannot be rendered
    relNoRender.forEach((relInfo) => relationInfo.delete(relInfo.id));
    relByDepth.forEach(attachRelationsToObjects);

    const objectsByKind = sortObjectsByKind(objectInfo);
    computeAttachPoints(objectsByKind);

    positionRelations(relationInfo);
};

/**
 * This funciton handles moving Objects around (e.g. argument, the arrow-tips and so on)
 */
module.exports.registerDragEvents = function (viewArea, liveView) {
    let relation = null;
    let where = null;
    let whereId = null;

    let initialX = null;
    let initialY = null;

    let initialClientX = null;
    let initialClientY = null;

    let oldX = null;
    let oldY = null;

    let what = null; // the type of "mosedown", eg. what is beeing moved
    let marker = null;

    let selectionDiv = null;
    const SNAP_TOLERANCE = 8;

    viewArea.addEventListener("mousedown", (e) => {
        let relationId = null;

        if (e.target.matches(SELECTOR_RELATION_DRAGGER)) {
            relationId = e.target.dataset.relationId;
        } else if (e.target.matches(SELECTOR_ARROW_TIP_DRAGGER)) {
            relationId = e.target.parentElement.parentElement.dataset.id;
        }

        if (relationId != null) {
            e.stopPropagation();

            const relationElement = liveView.el.querySelector(`g.relation-shadow-group[data-id="${relationId}"`);
            relation = new RelationInfo(relationElement, RECONSTRUCT_OBJECT_FROM, RECONSTRUCT_OBJECT_TO, liveView.el);
            relation.disableElementInteractions();

            if (e.target.dataset.fromTo == "from") {
                initialX = relation.fromX;
                initialY = relation.fromY;
                where = RECONSTRUCT_OBJECT_FROM;
            } else {
                initialX = relation.toX;
                initialY = relation.toY;
                where = RECONSTRUCT_OBJECT_TO;
            }

            initialClientX = e.clientX;
            initialClientY = e.clientY;

            whereId = relation.getObjectId(where);

            marker = e.target;
        }

        if (e.target.matches(SELECTOR_RELATION_DRAGGER)) {
            viewArea
                .querySelectorAll(
                    `${SELECTOR_CARD}[data-id="${whereId}"] ${SELECTOR_BORDER_MARKER}, ${SELECTOR_RELATION}[phx-value-id="${whereId}"]`,
                )
                .forEach((marker) => {
                    marker.classList.add(css.hoverBgBlue);
                    marker.classList.remove(css.disabled);
                });

            what = WHAT_DRAGGING_RELATION;
        } else if (e.target.matches(SELECTOR_ARROW_TIP_DRAGGER)) {
            ({ x: oldX, y: oldY } = relation.getArrowTipAdjust(marker.dataset.fromTo));

            what = WHAT_DRAGGING_ARROW_TIP;
            console.log("Start relation arrow tip drag action");

            // in case the "Left button" (=1) was pressed
        } else if (e.which === 1) {
            let isRelationOverlay = e.target === viewArea;
            if (isRelationOverlay) {
                let selectionOverlay = viewArea.querySelector("#selection-overlay");
                $(selectionOverlay)
                    .css("left", e.offsetX)
                    .css("top", e.offsetY)
                    .css("width", "0px")
                    .css("height", "0px")
                    .css("display", "block");
                // remember where mouse was clicked
                selectionDiv = { fromX: e.offsetX, fromY: e.offsetY };
            }
        }
    });

    viewArea.addEventListener("mousemove", (e) => {
        if (relation != null) {
            e.preventDefault();
            e.stopImmediatePropagation();

            // #1292 The user wanted to manually adjust the arrows, without any automatism
            // The solution was to allow the snappinhg when ALT is pressed.
            let snapTolerance = e.altKey ? SNAP_TOLERANCE : 0;

            if (what == WHAT_DRAGGING_RELATION) {
                let newX = initialX + (e.clientX - initialClientX);
                let newY = initialY + (e.clientY - initialClientY);

                const oppositeSide = relation.getOther(where);
                const targetY = relation.getCurrentY(oppositeSide);

                // We want the following behaviour: when the user is moving the relation line and they look almost aligned
                // then the computer should align them perfectly to a line.
                // check if lines are aligned horizontally
                if (Math.abs(newY - targetY) <= snapTolerance) {
                    // Don't ask me why the +1 is needed, the relations likes look perfectly straight with +1, otherwise it is slightly tilted.
                    newY = targetY + 1;
                }

                // check if lines are aligned veritcally
                const targetX = relation.getCurrentX(oppositeSide);
                if (Math.abs(newX - targetX) <= snapTolerance) {
                    // same as above. I checked with taking a screenshot and drawing a rectangle. duh.
                    newX = targetX + 1;
                }

                relation.setOverwritePosition(where, newX, newY);
            }

            // When users drags the arrow tips arounhd, then we have to rotate the both / and \ corectly.
            if (what == WHAT_DRAGGING_ARROW_TIP) {
                const diffX = e.clientX - initialClientX;
                const diffY = e.clientY - initialClientY;

                let angle = (relation.angle * Math.PI) / 180;

                let adjustX = oldX + (diffX * Math.cos(angle) + diffY * Math.sin(angle));
                let adjustY = oldY + (-1 * diffX * Math.sin(angle) + diffY * Math.cos(angle));

                if (utils.parseBool(marker.dataset.invert)) {
                    adjustY = -1 * adjustY;
                }

                if (marker.dataset.fromTo == "from") {
                    adjustX = -1 * adjustX;
                }

                relation.setArrowTipAdjust(marker.dataset.fromTo, adjustX, adjustY);
            }

            module.exports.positionRelations(liveView.el);
        } else {
            // on move
            if (selectionDiv) {
                let rect = viewArea.getBoundingClientRect();
                let offsetX = e.clientX - rect.left; //x position within the element.
                let offsetY = e.clientY - rect.top; //y position within the element.

                // a selection is happening, so adjust the size of the selection div
                let found = viewArea.querySelector("#selection-overlay");

                let largerX = offsetX > selectionDiv.fromX;
                let largerY = offsetY > selectionDiv.fromY;
                if (largerX && largerY) {
                    // move form top-left to bottom-right
                    $(found)
                        .css("width", offsetX - selectionDiv.fromX)
                        .css("height", offsetY - selectionDiv.fromY);
                } else if (largerX && !largerY) {
                    // move form bottom-left to top-right
                    $(found)
                        .css("left", e.fromX)
                        .css("top", offsetY)
                        .css("width", offsetX - selectionDiv.fromX)
                        .css("height", selectionDiv.fromY - offsetY);
                } else if (!largerX && largerY) {
                    $(found)
                        .css("left", offsetX)
                        .css("top", selectionDiv.fromY)
                        .css("width", selectionDiv.fromX - offsetX)
                        .css("height", offsetY - selectionDiv.fromY);
                } else if (!largerX && !largerY) {
                    $(found)
                        .css("left", offsetX)
                        .css("top", offsetY)
                        .css("width", selectionDiv.fromX - offsetX)
                        .css("height", selectionDiv.fromY - offsetY);
                }
            }
        }
    });

    viewArea.addEventListener("mouseup", (e) => {
        if (relation != null) {
            e.preventDefault();
            e.stopPropagation();

            if (what == WHAT_DRAGGING_RELATION) {
                let card = viewUtils.getCard(e);
                const meta = {
                    id: relation.id,
                    fromTo: where,
                };

                if (e.target.matches(SELECTOR_BORDER_MARKER) && e.target.parentElement.dataset.id == whereId) {
                    // target is card border
                    meta.border = e.target.id;
                    meta.target = "border";

                    const [xOffset, yOffset] = computeCardOffset(card, relation);

                    if (e.target.id == "left" || e.target.id == "right") {
                        meta.fraction = yOffset;
                    } else {
                        meta.fraction = xOffset;
                    }

                    liveView.pushEvent("object-move-end", meta);
                } else if (card != null && card.dataset.id == whereId) {
                    // target is hovering over card, keep position relative on this card
                    pushRelativeToCard(liveView, meta, card, relation);
                } else if (e.target.matches(SELECTOR_RELATION) && e.target.parentElement.dataset.id == whereId) {
                    // target is relation
                    const fromX = e.target.parentElement.dataset.fromX;
                    const fromY = e.target.parentElement.dataset.fromY;
                    const toX = e.target.parentElement.dataset.toX;
                    const toY = e.target.parentElement.dataset.toY;

                    const actualLength = Math.sqrt((fromX - toX) ** 2 + (fromY - toY) ** 2);

                    // bounds of view area container
                    const pageBounds = utils.boundingClientRelToBody(e.currentTarget);
                    const x = e.pageX - pageBounds.left;
                    const y = e.pageY - pageBounds.top;

                    const eventLength = Math.sqrt((fromX - x) ** 2 + (fromY - y) ** 2);

                    meta.fraction = eventLength / actualLength;
                    meta.target = "relation";

                    liveView.pushEvent("object-move-end", meta);
                } else {
                    // target is hovering somewhere in view area. Snap to closest card borders
                    card = viewArea.querySelector(`${SELECTOR_CARD}[data-id='${whereId}']`);
                    pushRelativeToCard(liveView, meta, card, relation);
                }

                viewArea.querySelectorAll(SELECTOR_BORDER_MARKERS).forEach((marker) => {
                    marker.classList.remove(css.hoverBgBlue);
                    marker.classList.add(css.disabled);
                });
            } else if (what == WHAT_DRAGGING_ARROW_TIP) {
                const fromTo = marker.dataset.fromTo;
                const { x: x, y: y } = relation.getArrowTipAdjust(fromTo);

                const data = {
                    id: relation.id,
                    type: "adjust_arrow_tip",
                    fromTo: fromTo,
                    x: x,
                    y: y,
                };

                liveView.pushEvent("object-adjust-end", data);
            } else {
                // reset whatever we might have done by sending esc
                liveView.pushEvent("esc-pressed");
            }

            relation.resetElementInteractions();
            relation = null;
            what = null;
            marker = null;
            oldX = null;
            oldY = null;
        } else {
            if (selectionDiv) {
                // selection ended! get selection area
                let selectionOverlay = viewArea.querySelector("#selection-overlay");
                let boundingClientRect = selectionOverlay.getBoundingClientRect();

                let relative_bounding = utils.getBoundingClientRectRelativeTo(selectionOverlay, viewArea);

                // get all cards in the view
                let found_cards = viewArea.querySelectorAll(".view-card,.subview-card");
                let card_ids = Array.from(found_cards)
                    // only keep the one which intersect selection
                    .filter((each) => utils.fullyContains(boundingClientRect, each.getBoundingClientRect()))
                    .map((each) => each.dataset.id);

                const data = {
                    card_ids: card_ids,
                    top: relative_bounding.top,
                    left: relative_bounding.left,
                    width: relative_bounding.width,
                    height: relative_bounding.height,
                };
                liveView.pushEvent("multi-selection", data);

                // remove selection
                selectionDiv = null;
            }
        }
    });

    function computeCardOffset(card, relation) {
        if (card == null) {
            console.trace("Card was null, show where it came from");
        }
        const bounds = card.getBoundingClientRect();

        if (card.classList.contains("subview-card")) {
            let parentBounding = utils.getBoundingClientRectRelativeToParent(card);

            // For sub-view-cards, the coordinates (relation.getCurrentX) of the relation has to be relative to the parent-card,
            // (because all coordinates of the sub-view cards are relative to the parent card), in this case, however
            // coordinate of the relations were relative to the parent-view, by subtracting parentBounding.left/parentBounding.top
            // we get the subview-card-parent relative coordinate

            const xOffset = (relation.getCurrentX(where) - parentBounding.left) / bounds.width;
            const yOffset = (relation.getCurrentY(where) - parentBounding.top) / bounds.height;
            return [utils.clamp(xOffset, 0, 1), utils.clamp(yOffset, 0, 1)];
        } else {
            // there's some slight rounding error in here with all the positions somewhere, so we need to manually
            // subtract 1 pixel from the positions, otherwise the arrow will jump by 1 pixel after being saved
            const xOffset = (relation.getCurrentX(where) - 1 - parseFloat(card.style.left)) / bounds.width;
            const yOffset = (relation.getCurrentY(where) - 1 - parseFloat(card.style.top)) / bounds.height;
            return [utils.clamp(xOffset, 0, 1), utils.clamp(yOffset, 0, 1)];
        }
    }

    function pushRelativeToCard(liveView, meta, card, relation) {
        const [xOffset, yOffset] = computeCardOffset(card, relation);

        meta.x = utils.clamp(xOffset, 0, 1);
        meta.y = utils.clamp(yOffset, 0, 1);
        meta.target = "card";

        liveView.pushEvent("object-move-end", meta);
    }
};
